<template>
    <v-container fluid class="px-0">
        <v-list flat>
            <v-list-item>
                <v-list-item-avatar>
                    <v-icon style="background-color: gainsboro !important; color: black">mdi-cog</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                    <v-list-item-title>
                        <strong style="font-size: 1rem">Settings</strong>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                        Preferences that apply to your account only.
                    </v-list-item-subtitle>
                </v-list-item-content>
            </v-list-item>
        </v-list>
        <v-divider />

        <v-row class="mt-4 px-4">
            <v-col cols="12" md="7" lg="5">
                <v-card outlined>
                    <v-card-title class="d-flex align-center">
                        <v-icon class="mr-2">mdi-earth</v-icon>
                        <span style="font-size: 1rem; font-weight: 700">Display timezone</span>
                    </v-card-title>
                    <v-card-subtitle>
                        Choose the timezone all dates and times are shown in. This
                        changes what you see only — nothing that gets saved changes.
                    </v-card-subtitle>
                    <v-card-text>
                        <v-autocomplete v-model="selected" :items="timezoneItems" dense outlined
                            label="Display times in" />
                        <div class="text-caption grey--text">
                            Company time is {{ $tz.baseline }} ({{ $tz.offsetLabel($tz.baseline) }})
                        </div>
                        <div class="text-caption grey--text mt-1">
                            It is currently {{ nowInSelected }} for you.
                        </div>
                        <div class="d-flex align-center flex-wrap mt-3">
                            <v-btn v-if="selected !== detected" text x-small class="text-none px-0 mr-4"
                                @click="selected = detected">
                                Use detected ({{ detected }})
                            </v-btn>
                            <v-btn v-if="selected !== $tz.baseline" text x-small class="text-none px-0"
                                @click="selected = $tz.baseline">
                                Reset to company time
                            </v-btn>
                        </div>
                    </v-card-text>
                </v-card>

                <v-card outlined class="mt-4">
                    <v-card-title class="d-flex align-center">
                        <v-icon class="mr-2">mdi-clock-outline</v-icon>
                        <span style="font-size: 1rem; font-weight: 700">Time format</span>
                    </v-card-title>
                    <v-card-subtitle>
                        Whether times are shown on a 12-hour or 24-hour clock.
                    </v-card-subtitle>
                    <v-card-text>
                        <v-btn-toggle v-model="timeFormat" mandatory dense color="primary">
                            <v-btn value="12" class="text-none px-4">12-hour</v-btn>
                            <v-btn value="24" class="text-none px-4">24-hour</v-btn>
                        </v-btn-toggle>
                        <div class="text-caption grey--text mt-3">
                            Example: {{ timeFormatExample }}
                        </div>
                    </v-card-text>
                </v-card>

                <div class="d-flex align-center mt-4">
                    <v-btn color="primary" class="text-none" :disabled="!dirty" :loading="saving" @click="save">
                        <v-icon left small>mdi-content-save</v-icon>
                        Save changes
                    </v-btn>
                    <v-btn v-if="dirty" text class="text-none ml-2" @click="discard">
                        Discard
                    </v-btn>
                    <span v-if="dirty" class="text-caption grey--text ml-3">
                        Unsaved changes
                    </span>
                </div>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'SettingsComponent',
    data() {
        return { ...this.draftFromStore(), saving: false };
    },
    computed: {
        ...mapGetters({ get_user: 'auth/get_user' }),
        detected() {
            return this.$date.tz.guess();
        },
        savedTimezone() {
            return this.$tz.display();
        },
        savedTimeFormat() {
            return this.$store.state.settings.timeFormat === '24' ? '24' : '12';
        },
        dirty() {
            return this.selected !== this.savedTimezone
                || this.timeFormat !== this.savedTimeFormat;
        },
        // Previews below read the DRAFT rather than the store, so the user sees
        // what they are about to save instead of what is still in effect.
        draftTimeToken() {
            return this.timeFormat === '24' ? 'HH:mm' : 'h:mm A';
        },
        nowInSelected() {
            return this.$date().tz(this.selected).format(`ddd, MMM D, YYYY ${this.draftTimeToken}`);
        },
        timeFormatExample() {
            const at = (h, m) => this.$date.utc().hour(h).minute(m).format(this.draftTimeToken);
            return `${at(9, 5)} and ${at(17, 30)}`;
        },
        // Full IANA list where the browser supports it (Chrome/Edge 99+,
        // Firefox 122+); falls back to the two zones we always need.
        timezoneItems() {
            try {
                return Intl.supportedValuesOf('timeZone');
            } catch (_) {
                return [this.$tz.baseline, this.detected];
            }
        },
    },
    methods: {
        draftFromStore() {
            return {
                selected: this.$tz.display(),
                timeFormat: this.$store.state.settings.timeFormat === '24' ? '24' : '12',
            };
        },
        // Applies locally first so the UI updates instantly, then syncs to the
        // user record so the preference follows the account to another machine.
        // A sync failure is reported but not rolled back: the local setting is
        // still valid for this browser, and losing it would be worse.
        async save() {
            this.$store.dispatch('settings/setTimezone', this.selected);
            this.$store.dispatch('settings/setTimeFormat', this.timeFormat);

            // The endpoint scopes to the authenticated user, so this is only a
            // "are we signed in at all" guard.
            const signedIn = !!(this.get_user && this.get_user.id);
            if (!signedIn) {
                this.$toast.success('Settings saved on this device');
                return;
            }

            this.saving = true;
            try {
                // Dedicated endpoint: update_user requires role_id/name/email,
                // so a partial save of just these two fields would be rejected.
                // It scopes to the authenticated user and takes no id.
                const { data } = await this.$axios.post('api/users/update_preferences', {
                    timezone: this.selected,
                    time_format: this.timeFormat,
                });
                if (data && data.response === false) throw new Error(data.message || 'rejected');

                // Keep the cached user in step so a reload hydrates the new values.
                this.$store.dispatch('auth/set_user', {
                    ...this.get_user,
                    timezone: this.selected,
                    time_format: this.timeFormat,
                });
                this.$toast.success('Settings saved to your account');
            } catch (error) {
                console.error('Could not sync settings to the account:', error);
                this.$toast.warning('Saved on this device only — could not reach the server');
            } finally {
                this.saving = false;
            }
        },
        discard() {
            Object.assign(this, this.draftFromStore());
        },
    },
};
</script>
